import {
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  ElementRef,
  inject,
  OnDestroy,
  signal,
  untracked,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { extend, injectBeforeRender, injectStore, NgtArgs } from 'angular-three';
import { NgtsOrbitControls } from 'angular-three-soba/controls';
import * as THREE from 'three';
import {
  SphereGeometry,
  PlaneGeometry,
  CylinderGeometry,
  RingGeometry,
  ConeGeometry,
  TorusGeometry,
  MeshStandardMaterial,
  AmbientLight,
  DirectionalLight,
  HemisphereLight,
  Vector3,
} from 'three';
import { ArService } from '../../services/ar.service';
import type { SportsGameConfig, ShotInput } from './sports.types';
import type {
  ShotEvaluation,
  StartingPosition,
  SportPhysicsParams,
} from './sport-definitions/sport-definition.interface';
import * as CANNON from 'cannon-es';
import { SportDefinitionRegistry } from './sport-definitions/sport-definition.registry';
import { SimplifiedPhysicsEngine } from './sports-physics';

// ─── Three.js registration ─────────────────────────────────────────────────────
// Must be called at module level before Angular Three processes ngt-* elements.
extend({
  SphereGeometry,
  PlaneGeometry,
  CylinderGeometry,
  RingGeometry,
  ConeGeometry,
  TorusGeometry,
  MeshStandardMaterial,
  AmbientLight,
  DirectionalLight,
  HemisphereLight,
  Vector3,
});

// ─── Distance ring config ──────────────────────────────────────────────────────

interface DistanceRing {
  /** Negative Z (forward in Three.js) in scene units */
  z: number;
  /** Lateral X offset — randomized each game so rings aren't always centred */
  x: number;
  innerRadius: number;
  outerRadius: number;
  color: string;
  label: string;
}

/** Phase 1: golf driving distance rings. Scaled to match golf-driving launchSpeedScale=2.0.
 *  x offsets are placeholders — overwritten each attempt by randomizeRingOffsets(). */
const GOLF_DISTANCE_RINGS: DistanceRing[] = [
  { z: -1.0, x: 0, innerRadius: 0.49, outerRadius: 0.52, color: '#4CAF50', label: 'Good'      },
  { z: -2.5, x: 0, innerRadius: 0.49, outerRadius: 0.52, color: '#2196F3', label: 'Great'     },
  { z: -4.0, x: 0, innerRadius: 0.49, outerRadius: 0.52, color: '#FF9800', label: 'Excellent' },
];

/**
 * Returns a copy of the base rings with independent random lateral offsets AND
 * slightly jittered forward distances, so each attempt presents a different layout.
 *
 * Z jitter (forward distance) per band — keeps bands meaningfully separated:
 *   - Good:      -0.80 … -1.20   (±0.20 around z=-1.0)
 *   - Great:     -2.20 … -2.80   (±0.30 around z=-2.5)
 *   - Excellent: -3.70 … -4.30   (±0.30 around z=-4.0)
 *
 * Lateral (X) offsets grow with distance so far rings reward precision aiming.
 */
function randomizeRingOffsets(): DistanceRing[] {
  const rand  = (max: number) => (Math.random() * 2 - 1) * max;
  return [
    { ...GOLF_DISTANCE_RINGS[0], z: -1.0 + rand(0.20), x: rand(0.20) },
    { ...GOLF_DISTANCE_RINGS[1], z: -2.5 + rand(0.30), x: rand(0.45) },
    { ...GOLF_DISTANCE_RINGS[2], z: -4.0 + rand(0.30), x: rand(0.75) },
  ];
}

// ─── Camera configs (per sport) ───────────────────────────────────────────────

interface CameraConfig {
  position: [number, number, number];
  lookAt: [number, number, number];
}

/** First-person camera configs per sport. */
const CAMERA_CONFIGS: Record<string, CameraConfig> = {
  // Ground-level view looking forward + slightly up — arc rises visibly above horizon.
  'golf-driving': { position: [0, 0.2, 1.5], lookAt: [0, 1.0, -4.0] },
  'baseball':     { position: [0, 0.2, 1.5], lookAt: [0, 1.0, -4.0] },
  // Football/soccer/hockey: eye-level behind ball, goal ahead at appropriate height.
  'football':     { position: [0, 0.4, 2.8], lookAt: [0, 2.0, -2.5] },
  // Soccer: camera pulled well back to match the longer penalty-spot distance.
  // Ball is at z=0, goal at z≈-4.5. Camera at z=5.5 gives a behind-the-ball view
  // with the goal visible in the mid-distance — similar to the freekick reference.
  'soccer':       { position: [0, 0.55, 5.5], lookAt: [0, 0.5, -4.5] },
  'ice-hockey':   { position: [0, 0.15, 2.0], lookAt: [0, 0.3, -2.0] },
  // Basketball: slightly elevated, looking up toward the hoop.
  'basketball':   { position: [0, 0.9, 2.8], lookAt: [0, 2.5, -2.0] },
};
const CAMERA_DEFAULT: CameraConfig = { position: [0, 0.2, 1.5], lookAt: [0, 1.0, -4.0] };

// ─── Animation constants ───────────────────────────────────────────────────────

const ARC_STEPS = 60;
const ARC_DURATION_MS = 2000;
const RESULT_DISPLAY_MS = 1500;

// ─── Component ─────────────────────────────────────────────────────────────────

/**
 * Sports Scene Component — Phase 1 (Golf Driving, non-AR)
 *
 * Responsibilities:
 *   - Procedural 3D environment: ground, tee, ball, distance rings.
 *   - First-person camera behind the ball looking down the fairway.
 *   - Pointer/touch swipe gesture → ShotInput (direction + power).
 *   - Exposes scene.userData callbacks for the renderer:
 *       awaitNextShot()          → Promise<ShotInput>
 *       animateShot(evaluation)  → Promise<void>
 *       setPosition(position)    → Promise<void>
 *       resetGame()              → void
 *
 * Asset-swap hook:
 *   The ball mesh uses a procedural sphere (radius = physicsParams.ballRadius).
 *   When ISportDefinition.assets.ballModelPath is present, this component
 *   should load the GLTF and replace the sphere — zero physics changes needed.
 *   (Phase 3 visual upgrade task.)
 */
@Component({
  selector: 'lib-sports-scene',
  imports: [NgtArgs, NgtsOrbitControls],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <!-- ── Lighting ──────────────────────────────────────────────────────── -->
    <ngt-ambient-light [intensity]="0.7" />
    <ngt-hemisphere-light
      [skyColor]="8839915"
      [groundColor]="2972187"
      [intensity]="0.6"
    />
    <ngt-directional-light
      [position]="[5, 10, 5]"
      [intensity]="2.0"
      [castShadow]="true"
    />
    <ngt-directional-light
      [position]="[-3, 6, -2]"
      [intensity]="0.6"
    />

    <!-- ── Orbit controls (preview / AR-bypass only) ──────────────────── -->
    @if (enableCameraControls()) {
      <ngts-orbit-controls
        [options]="{
          enableDamping: true,
          dampingFactor: 0.05,
          enablePan: false,
          maxPolarAngle: 1.4
        }"
      />
    }

    <!-- ── Scene group (AR rotation applied here) ────────────────────── -->
    <ngt-group [rotation]="sceneGroupRotation()">

      <!-- Ground plane: 40 × 80 scene units; color varies by sport -->
      <ngt-mesh
        [rotation]="[-1.5707963, 0, 0]"
        [position]="[0, 0, 0]"
        [receiveShadow]="true"
      >
        <ngt-plane-geometry *args="[40, 80]" />
        <ngt-mesh-standard-material
          [color]="groundColor()"
          [roughness]="0.9"
          [metalness]="0.0"
        />
      </ngt-mesh>

      <!-- Tee peg (golf only) -->
      @if (showTee()) {
        <ngt-mesh [position]="[0, 0.025, 0]" [castShadow]="true">
          <ngt-cylinder-geometry *args="[0.03, 0.04, 0.05, 12]" />
          <ngt-mesh-standard-material [color]="'#f5f5dc'" />
        </ngt-mesh>
      }

      <!-- Distance rings (golf-driving + baseball) -->
      @for (ring of distanceRings(); track ring.z) {
        <ngt-mesh
          [rotation]="[-1.5707963, 0, 0]"
          [position]="[ring.x, 0.005, ring.z]"
        >
          <ngt-ring-geometry *args="[ring.innerRadius, ring.outerRadius, 64]" />
          <ngt-mesh-standard-material
            [color]="ring.color"
            [transparent]="true"
            [opacity]="0.65"
          />
        </ngt-mesh>
      }

      <!-- ── Soccer goal ──────────────────────────────────────────────── -->
      @if (sportDef()?.id === 'soccer') {
        <!-- Left post -->
        <ngt-mesh [position]="[-0.60, 0.40, goalZ()]" [castShadow]="true">
          <ngt-cylinder-geometry *args="[0.025, 0.025, 0.80, 12]" />
          <ngt-mesh-standard-material [color]="'#ffffff'" />
        </ngt-mesh>
        <!-- Right post -->
        <ngt-mesh [position]="[0.60, 0.40, goalZ()]" [castShadow]="true">
          <ngt-cylinder-geometry *args="[0.025, 0.025, 0.80, 12]" />
          <ngt-mesh-standard-material [color]="'#ffffff'" />
        </ngt-mesh>
        <!-- Crossbar -->
        <ngt-mesh [position]="[0, 0.80, goalZ()]" [rotation]="[0, 0, 1.5707963]" [castShadow]="true">
          <ngt-cylinder-geometry *args="[0.025, 0.025, 1.20, 12]" />
          <ngt-mesh-standard-material [color]="'#ffffff'" />
        </ngt-mesh>
        <!-- Net (transparent plane facing the player) -->
        <ngt-mesh [position]="[0, 0.40, goalZ()]">
          <ngt-plane-geometry *args="[1.20, 0.80]" />
          <ngt-mesh-standard-material [color]="'#ffffff'" [transparent]="true" [opacity]="0.25" [side]="2" />
        </ngt-mesh>
      }

      <!-- ── Football uprights ────────────────────────────────────────── -->
      @if (sportDef()?.id === 'football') {
        <!-- Center pole (ground to crossbar) -->
        <ngt-mesh [position]="[0, 1.0, goalZ()]" [castShadow]="true">
          <ngt-cylinder-geometry *args="[0.03, 0.03, 2.0, 10]" />
          <ngt-mesh-standard-material [color]="'#FFD700'" />
        </ngt-mesh>
        <!-- Crossbar (horizontal, at top of center pole) -->
        <ngt-mesh [position]="[0, 2.0, goalZ()]" [rotation]="[0, 0, 1.5707963]" [castShadow]="true">
          <ngt-cylinder-geometry *args="[0.025, 0.025, 0.70, 10]" />
          <ngt-mesh-standard-material [color]="'#FFD700'" />
        </ngt-mesh>
        <!-- Left upright (extends upward from crossbar end) -->
        <ngt-mesh [position]="[-0.35, 2.6, goalZ()]" [castShadow]="true">
          <ngt-cylinder-geometry *args="[0.025, 0.025, 1.2, 10]" />
          <ngt-mesh-standard-material [color]="'#FFD700'" />
        </ngt-mesh>
        <!-- Right upright -->
        <ngt-mesh [position]="[0.35, 2.6, goalZ()]" [castShadow]="true">
          <ngt-cylinder-geometry *args="[0.025, 0.025, 1.2, 10]" />
          <ngt-mesh-standard-material [color]="'#FFD700'" />
        </ngt-mesh>
      }

      <!-- ── Ice Hockey net ───────────────────────────────────────────── -->
      @if (sportDef()?.id === 'ice-hockey') {
        <!-- Left post -->
        <ngt-mesh [position]="[-0.70, 0.225, goalZ()]" [castShadow]="true">
          <ngt-cylinder-geometry *args="[0.022, 0.022, 0.45, 10]" />
          <ngt-mesh-standard-material [color]="'#DD0000'" />
        </ngt-mesh>
        <!-- Right post -->
        <ngt-mesh [position]="[0.70, 0.225, goalZ()]" [castShadow]="true">
          <ngt-cylinder-geometry *args="[0.022, 0.022, 0.45, 10]" />
          <ngt-mesh-standard-material [color]="'#DD0000'" />
        </ngt-mesh>
        <!-- Crossbar -->
        <ngt-mesh [position]="[0, 0.45, goalZ()]" [rotation]="[0, 0, 1.5707963]" [castShadow]="true">
          <ngt-cylinder-geometry *args="[0.022, 0.022, 1.40, 10]" />
          <ngt-mesh-standard-material [color]="'#DD0000'" />
        </ngt-mesh>
        <!-- Net (transparent, facing player) -->
        <ngt-mesh [position]="[0, 0.225, goalZ()]">
          <ngt-plane-geometry *args="[1.40, 0.45]" />
          <ngt-mesh-standard-material [color]="'#ffffff'" [transparent]="true" [opacity]="0.30" [side]="2" />
        </ngt-mesh>
      }

      <!-- ── Basketball hoop + backboard ─────────────────────────────── -->
      @if (sportDef()?.id === 'basketball') {
        <!-- Backboard (slightly behind the hoop) -->
        <ngt-mesh [position]="[0, 2.20, goalZ() + 0.10]">
          <ngt-plane-geometry *args="[0.65, 0.45]" />
          <ngt-mesh-standard-material [color]="'#ffffff'" [transparent]="true" [opacity]="0.75" [side]="2" />
        </ngt-mesh>
        <!-- Backboard frame border (solid rectangle — two short + two tall cylinders) -->
        <!-- Hoop ring (torus lying flat) -->
        <ngt-mesh [position]="[0, 2.05, goalZ()]" [rotation]="[1.5707963, 0, 0]">
          <ngt-torus-geometry *args="[0.22, 0.018, 8, 32]" />
          <ngt-mesh-standard-material [color]="'#FF6B00'" [roughness]="0.4" [metalness]="0.6" />
        </ngt-mesh>
      }

      <!-- Ball — position controlled directly via #ballMesh in injectBeforeRender.
           During physics animation the mesh position is set from cannon-es each frame
           (same as freekick animate() → ball.position.copy(ballBody.position)).
           During idle, injectBeforeRender keeps it in sync with ballPosition(). -->
      <ngt-mesh
        #ballMesh
        [castShadow]="true"
      >
        <ngt-sphere-geometry *args="[ballRadius(), 24, 24]" />
        <ngt-mesh-standard-material
          [color]="ballColor()"
          [roughness]="0.3"
          [metalness]="0.05"
        />
      </ngt-mesh>

      <!-- Swipe-up hint: two stacked cones shown while waiting for player input -->
      @if (showSwipeHint()) {
        <ngt-mesh [position]="[ballPosition()[0], ballPosition()[1] + ballRadius() + 0.42, ballPosition()[2]]">
          <ngt-cone-geometry *args="[0.07, 0.16, 8]" />
          <ngt-mesh-standard-material [color]="'#ffffff'" [transparent]="true" [opacity]="0.90" />
        </ngt-mesh>
        <ngt-mesh [position]="[ballPosition()[0], ballPosition()[1] + ballRadius() + 0.24, ballPosition()[2]]">
          <ngt-cone-geometry *args="[0.055, 0.12, 8]" />
          <ngt-mesh-standard-material [color]="'#ffffff'" [transparent]="true" [opacity]="0.55" />
        </ngt-mesh>
      }

    </ngt-group>

  `,
  styles: [':host { display: contents; }'],
})
export class SportsSceneComponent implements OnDestroy {
  // ─── DI ──────────────────────────────────────────────────────────────────────
  private store = injectStore();
  private arService = inject(ArService);
  private physics = new SimplifiedPhysicsEngine();

  // ─── OrbitControls reference (needed for camera lock in Experience Preview) ──
  private orbitControlsRef = viewChild(NgtsOrbitControls);

  // ─── State signals ────────────────────────────────────────────────────────────
  protected ballPosition = signal<[number, number, number]>([0, 0.05, 0]);
  private animating = false;

  // ─── Swipe input state ────────────────────────────────────────────────────────
  private swipeStart: { x: number; y: number; time: number } | null = null;
  private pendingShotResolve: ((input: ShotInput) => void) | null = null;
  private listeningForShot = false;

  // ─── Pointer event cleanup ────────────────────────────────────────────────────
  private canvas: HTMLCanvasElement | null = null;
  private boundPointerDown = this.onPointerDown.bind(this);
  private boundPointerUp = this.onPointerUp.bind(this);
  // ─── Swipe hint ──────────────────────────────────────────────────────────────
  protected showSwipeHint = signal(false);

  // ─── Camera aim (captured at pointer-down; applied to trajectory in world space) ──
  // Default: camera behind ball looking down -Z (the scene's initial orientation).
  private shotCameraForward = { x: 0, z: -1 };
  // right = cross(forward, worldUp) projected to XZ = { x: -fz, z: fx }
  private shotCameraRight = { x: 1, z: 0 };

  // ─── cannon-es physics world (shared across shots; reset between shots) ──────────────
  // Physics is stepped via injectBeforeRender so it's synchronized with the
  // Three.js render loop (same approach as freekick example's animate() loop).
  private cannonWorld: CANNON.World | null = null;
  private cannonBallBody: CANNON.Body | null = null;
  private physicsActive = false;
  private physicsGoalZ = 0;
  private physicsGoingNegZ = true;
  private physicsEndPos = { x: 0, y: 0, z: 0 };
  private physicsOnDone: (() => void) | null = null;

  // ─── Ball mesh ref (accessed in injectBeforeRender for direct position control) ──
  // Bypasses Angular signal re-rendering during physics for maximum accuracy.
  readonly ballMesh = viewChild<ElementRef<THREE.Mesh>>('ballMesh');

  protected config = computed<SportsGameConfig | null>(() => {
    const scene = untracked(() => this.store.get()).scene;
    const configSignal = scene?.userData?.['configSignal'];
    return configSignal ? (configSignal() as SportsGameConfig) : null;
  });

  protected sportDef = computed(() => {
    const cfg = this.config();
    if (!cfg?.settings?.sportType) return null;
    try {
      return SportDefinitionRegistry.get(cfg.settings.sportType);
    } catch {
      return null;
    }
  });

  protected physicsParams = computed<SportPhysicsParams | null>(() =>
    this.sportDef()?.physicsParams ?? null
  );

  protected ballRadius = computed(() => this.physicsParams()?.ballRadius ?? 0.05);
  protected ballColor = computed(() => this.sportDef()?.assets.ballColor ?? '#ffffff');

  // ─── Distance rings (randomized lateral offset per game) ───────────────────
  private ringLayout = signal<DistanceRing[]>(randomizeRingOffsets());

  protected distanceRings = computed<DistanceRing[]>(() => {
    const def = this.sportDef();
    if (!def) return [];
    // Distance-bands sports (golf-driving, baseball) use randomized ring layout.
    return def.scoringModel === 'distance-bands' ? this.ringLayout() : [];
  });

  // ─── Goal geometry state ────────────────────────────────────────────────────

  /** Forward distance to the goal for the current attempt (scene units). */
  private currentAttemptDistance = signal<number>(2.0);

  /** Z position of the goal in the scene (negative = forward). */
  protected goalZ = computed(() => -this.currentAttemptDistance());

  /** True for sports where the ball must enter a goal (not land at distance). */
  protected isGoalSport = computed(() => this.sportDef()?.scoringModel !== 'distance-bands');

  /** Ground surface color keyed by sport. */
  protected groundColor = computed<string>(() => {
    switch (this.sportDef()?.id) {
      case 'basketball': return '#C68642';   // hardwood court
      case 'ice-hockey': return '#dff0f8';   // ice surface
      default:           return '#2d8b45';   // fairway / grass
    }
  });

  /** Whether to show the tee peg (only distance-bands sports on a tee). */
  protected showTee = computed(() => {
    const id = this.sportDef()?.id;
    return id === 'golf-driving'; // baseball has no tee peg
  });

  protected enableCameraControls = computed(() => {
    const scene = untracked(() => this.store.get()).scene;
    const fromUserData = scene?.userData?.['enableCameraControls'] ?? false;
    return this.arService.shouldBypassAR || fromUserData;
  });

  /**
   * True when running inside the portal studio preview (previewMode=true on GameEngine).
   * In this mode pointer input uses mouse-friendly thresholds instead of touch-swipe thresholds.
   */
  protected isPreviewMode = computed(() => {
    const scene = untracked(() => this.store.get()).scene;
    return scene?.userData?.['previewMode'] === true;
  });

  protected enableAR = computed(() => {
    const scene = untracked(() => this.store.get()).scene;
    const fromUserData = scene?.userData?.['enableAR'] ?? false;
    return fromUserData && !this.arService.shouldBypassAR;
  });

  protected sceneGroupRotation = computed<[number, number, number]>(() => {
    const def = this.sportDef();
    if (this.enableAR() && def) return def.arSceneRotation;
    return [0, 0, 0];
  });

  /** Per-sport first-person camera position + lookAt target. */
  protected cameraConfig = computed<CameraConfig>(() => {
    const id = this.sportDef()?.id ?? '';
    return CAMERA_CONFIGS[id] ?? CAMERA_DEFAULT;
  });

  // ─── Constructor: effects + scene setup ─────────────────────────────────────

  constructor() {
    // Register userData callbacks once the canvas store is ready
    effect(() => {
      const storeState = this.store.get();
      const scene = storeState?.scene;
      if (!scene) return;

      // Always re-register when scene changes (e.g. canvas recreation during HMR)
      untracked(() => this.registerSceneCallbacks(scene));
    });

    // Store OrbitControls reference in scene.userData so ExperiencePreview can lock/unlock it
    effect(() => {
      const controls = this.orbitControlsRef();
      if (!controls) return;
      const scene = untracked(() => this.store.get()).scene;
      if (!scene) return;
      const orbitControls = controls.controls();
      if (orbitControls) {
        scene.userData['orbitControls'] = orbitControls;
      }
    });

    // Set camera to sport-appropriate first-person position.
    // Re-fires when the sport definition changes so switching sport type from the
    // configurator immediately adjusts the viewpoint.
    effect(() => {
      const config = this.cameraConfig(); // reactive: fires on sport change
      const camera = untracked(() => this.store.get()).camera;
      if (!camera) return;

      untracked(() => {
        camera.position.set(...config.position);
        camera.lookAt(...config.lookAt);
        camera.updateMatrixWorld();
      });
    });

    // Sync ball Y offset whenever ballRadius changes (so it rests on the ground)
    effect(() => {
      const r = this.ballRadius();
      const current = this.ballPosition();
      if (Math.abs(current[1] - r) > 0.001) {
        this.ballPosition.set([current[0], r, current[2]]);
      }
    });

    // ── Physics render loop ─────────────────────────────────────────────────
    // Mirrors the freekick example's animate() loop:
    //   world.step(1/60, delta, 3)         ← step physics
    //   ball.position.copy(ballBody.position)  ← sync mesh
    // Running inside injectBeforeRender guarantees the physics step and the
    // Three.js render happen in the same frame, eliminating the two-rAF-loop
    // desync that caused the ball to appear stationary.
    injectBeforeRender(({ delta }) => {
      const mesh = this.ballMesh()?.nativeElement;
      if (!mesh) return;

      if (this.physicsActive && this.cannonWorld && this.cannonBallBody) {
        // Step physics by the actual frame delta (capped at 1/30 to avoid spiral of death)
        this.cannonWorld.fixedStep(1 / 60, Math.min(delta, 1 / 30));

        const p = this.cannonBallBody.position;
        mesh.position.set(p.x, p.y, p.z);

        // Goal plane crossing detection
        const crossed = this.physicsGoingNegZ
          ? p.z <= this.physicsGoalZ
          : p.z >= this.physicsGoalZ;

        if (crossed) {
          // Snap to evaluation's exact landing position so scoring is consistent
          const r = (this.cannonBallBody.shapes[0] as CANNON.Sphere).radius;
          mesh.position.set(
            this.physicsEndPos.x,
            Math.max(r, this.physicsEndPos.y),
            this.physicsEndPos.z
          );
          // Update signal so swipe-hint cones (idle state) are positioned correctly
          this.ballPosition.set([mesh.position.x, mesh.position.y, mesh.position.z]);

          // ── DEBUG ──────────────────────────────────────────────────────────────
          console.log('[LAND] Ball reached goal plane:', {
            physicsPos: { x: +p.x.toFixed(3), y: +p.y.toFixed(3), z: +p.z.toFixed(3) },
            snappedTo: {
              x: +mesh.position.x.toFixed(3),
              y: +mesh.position.y.toFixed(3),
              z: +mesh.position.z.toFixed(3),
            },
            goalZ: +this.physicsGoalZ.toFixed(3),
          });

          this.physicsActive = false;
          const done = this.physicsOnDone;
          this.physicsOnDone = null;
          if (done) done();
        }
      } else {
        // Idle state: keep mesh in sync with the signal (setPosition / reset)
        const pos = this.ballPosition();
        mesh.position.set(pos[0], pos[1], pos[2]);
      }
    });
  }

  ngOnDestroy(): void {
    this.physicsActive = false;
    this.physicsOnDone = null;
    this.cannonWorld = null;
    this.cannonBallBody = null;
    this.removePointerListeners();
    // Clear userData to avoid stale callbacks after component teardown
    const scene = untracked(() => this.store.get()).scene;
    if (scene) {
      delete scene.userData['awaitNextShot'];
      delete scene.userData['animateShot'];
      delete scene.userData['setPosition'];
      delete scene.userData['resetGame'];
      delete scene.userData['onCameraLockChanged'];
      delete scene.userData['onCameraReset'];
      delete scene.userData['orbitControls'];
    }
  }

  // ─── Scene callbacks (called by SportsRenderer via scene.userData) ──────────

  private registerSceneCallbacks(scene: THREE.Scene): void {
    // Attach canvas pointer listeners for swipe input
    const gl = (this.store.get() as any)?.gl;
    const canvas = gl?.domElement as HTMLCanvasElement | undefined;
    if (canvas && canvas !== this.canvas) {
      this.removePointerListeners();
      this.canvas = canvas;
      this.canvas.addEventListener('pointerdown', this.boundPointerDown, { passive: true });
      this.canvas.addEventListener('pointerup', this.boundPointerUp, { passive: true });
    }

    scene.userData['awaitNextShot'] = (): Promise<ShotInput> => {
      // Camera lock is managed by the player via the lock icon — we never
      // touch OrbitControls.enabled here. If the camera is already locked
      // when this fires (e.g. player kept it locked between attempts), arm
      // immediately so there's no dead window between shots.
      return new Promise<ShotInput>((resolve) => {
        this.pendingShotResolve = resolve;
        const orbitControls = scene.userData['orbitControls'];
        const alreadyLocked = orbitControls ? !orbitControls.enabled : false;
        if (alreadyLocked) {
          this.listeningForShot = true;
          this.showSwipeHint.set(true);
        }
      });
    };

    scene.userData['animateShot'] = (evaluation: ShotEvaluation): Promise<void> => {
      return this.animateShotArc(evaluation);
    };

    scene.userData['setPosition'] = (position: StartingPosition): Promise<void> => {
      const r = this.ballRadius();
      this.ballPosition.set([position.lateralOffset, r, 0]);
      // Track goal distance so the goal geometry renders at the correct Z.
      this.currentAttemptDistance.set(position.distance);
      // Reset physics ball to new starting position
      this.physicsActive = false;
      this.physicsOnDone = null;
      if (this.cannonBallBody) {
        this.cannonBallBody.position.set(position.lateralOffset, r, 0);
        this.cannonBallBody.velocity.set(0, 0, 0);
        this.cannonBallBody.angularVelocity.set(0, 0, 0);
      }
      // Only shuffle rings for distance-bands sports (golf, baseball).
      if (this.sportDef()?.scoringModel === 'distance-bands') {
        this.ringLayout.set(randomizeRingOffsets());
      }
      // Reset camera to sport-appropriate position and save as the OrbitControls
      // "home" state so subsequent reset() calls return here.
      const orbitControls = scene.userData['orbitControls'];
      const camera = (this.store.get() as any)?.camera as THREE.Camera | undefined;
      if (camera) {
        const cfg = this.cameraConfig();
        camera.position.set(...cfg.position);
        camera.lookAt(...cfg.lookAt);
        camera.updateMatrixWorld();
      }
      if (orbitControls) {
        orbitControls.saveState();
        orbitControls.enabled = true;
      }
      if (typeof scene.userData['onCameraReset'] === 'function') {
        scene.userData['onCameraReset']();
      }
      return Promise.resolve();
    };

    scene.userData['resetGame'] = (): void => {
      // Shuffle ring positions for the next game so each play-through is unique.
      if (this.sportDef()?.scoringModel === 'distance-bands') {
        this.ringLayout.set(randomizeRingOffsets());
      }
      this.showSwipeHint.set(false);
      const r = this.ballRadius();
      this.ballPosition.set([0, r, 0]);
      // Stop any in-flight physics
      this.physicsActive = false;
      this.physicsOnDone = null;
      if (this.cannonBallBody) {
        this.cannonBallBody.position.set(0, r, 0);
        this.cannonBallBody.velocity.set(0, 0, 0);
        this.cannonBallBody.angularVelocity.set(0, 0, 0);
      }
      this.listeningForShot = false;
      this.pendingShotResolve = null;
      this.animating = false;
      // Reset camera to sport-appropriate home position.
      const orbitControls = scene.userData['orbitControls'];
      const camera = (this.store.get() as any)?.camera as THREE.Camera | undefined;
      if (camera) {
        const cfg = this.cameraConfig();
        camera.position.set(...cfg.position);
        camera.lookAt(...cfg.lookAt);
        camera.updateMatrixWorld();
      }
      if (orbitControls) {
        orbitControls.saveState();
        orbitControls.enabled = true;
      }
      if (typeof scene.userData['onCameraReset'] === 'function') {
        scene.userData['onCameraReset']();
      }
    };

    // Called by ExperiencePreview when the player clicks the lock icon.
    // Armed = camera locked (OrbitControls disabled) → swiping shoots the ball.
    // Disarmed = camera unlocked (OrbitControls enabled) → swiping orbits the camera.
    scene.userData['onCameraLockChanged'] = (locked: boolean): void => {
      if (locked) {
        // Only arm if a shot is actually pending (game is in progress)
        if (this.pendingShotResolve) {
          this.listeningForShot = true;
          this.showSwipeHint.set(true);
        }
      } else {
        this.listeningForShot = false;
        this.showSwipeHint.set(false);
      }
    };

  }

  // ─── Swipe gesture ─────────────────────────────────────────────────────────────

  private onPointerDown(event: PointerEvent): void {
    if (!this.listeningForShot || this.animating) return;
    this.swipeStart = { x: event.clientX, y: event.clientY, time: performance.now() };

    // Capture camera aim direction at the moment the player starts their shot.
    // This lets orbit controls be used for aiming: orbit to face where you want
    // to hit, then swipe. The trajectory is rotated into world space in animateShotArc.
    const camera = (this.store.get() as any)?.camera as THREE.Camera | undefined;
    if (camera) {
      const fw = new THREE.Vector3();
      camera.getWorldDirection(fw);
      fw.y = 0;
      if (fw.lengthSq() > 0.0001) fw.normalize();
      this.shotCameraForward = { x: fw.x, z: fw.z };
      // right = cross(forward, worldUp) → { x: -fz, z: fx }
      this.shotCameraRight = { x: -fw.z, z: fw.x };

      // ── DEBUG ──────────────────────────────────────────────────────────────
      // Copy and paste this block from the browser console to file a bug report.
      console.log('[AIM] Camera captured at pointer-down:', {
        cameraPos: camera.position.toArray().map((v: number) => +v.toFixed(3)),
        fw: { x: +this.shotCameraForward.x.toFixed(3), z: +this.shotCameraForward.z.toFixed(3) },
        rt: { x: +this.shotCameraRight.x.toFixed(3), z: +this.shotCameraRight.z.toFixed(3) },
        ballPos: this.ballPosition().map(v => +v.toFixed(3)),
        goalZ: +(-this.currentAttemptDistance()).toFixed(3),
      });
    }
  }

  private onPointerUp(event: PointerEvent): void {
    if (!this.listeningForShot || !this.swipeStart || this.animating) return;

    const dx = event.clientX - this.swipeStart.x;
    const dy = event.clientY - this.swipeStart.y;  // negative = swipe up (forward)
    const dt = Math.max(1, performance.now() - this.swipeStart.time);
    this.swipeStart = null;

    const magnitude = Math.sqrt(dx * dx + dy * dy);
    const previewMode = this.isPreviewMode();

    let input: ShotInput;

    if (previewMode) {
      // ── Preview / mouse mode ────────────────────────────────────────────────
      // A mouse drag is slow and short compared to a touch swipe. Treat any
      // pointer-up as a valid shot — if the drag was tiny (a click), synthesize
      // a straight "good drive"; for actual drags, scale power to 500 px/s = full.
      if (magnitude < 5) {
        // Plain click: synthesize a weak tap that falls short of the first ring —
        // encourages the player to make a real swipe rather than clicking.
        // power 0.40 × 2.0 (launchSpeedScale) × cos(40°) ≈ 0.61 scene units (below Good at 1.0).
        input = { direction: { x: 0, y: 0.5 }, power: 0.10 };
        console.log('[SportsScene] PREVIEW click → synthesized shot', input);
      } else {
        const nx = dx / magnitude;
        const ny = dy / magnitude;
        const speed = magnitude / (dt / 1000);
        // 400 px/s = power 1.0 ("full" swing). Power is not capped — swiping
        // faster produces power > 1.0, making the ball fly further; overshoot
        // the far boundary and it's out of bounds. Over-swing (≥5.5×) is a whiff.
        // Cap at 1.0 so preview mouse drags stay within the sport's expected power range.
        // 500 px/s = full power (1.0); faster drags are capped, not punished.
        const power = Math.min(1.0, Math.max(0.1, speed / 500));
        // Over-swing: only a very wild drag (2800 px/s+) causes a whiff.
        const overSwing = speed > 2800;
        input = {
          direction: {
            x: nx,
            // Shift offset so a natural upward swipe (~30° off vertical)
            // lands at direction.y=0.5 (optimal elevation), and a perfectly
            // straight-up swipe gives direction.y=0.75 (60°) — well below
            // the whiff threshold. Old formula had horizontal = optimal which
            // felt wrong for a swipe-up gesture.
            y: Math.max(0, Math.min(1, -ny * 0.5 + 0.25)),
          },
          power,
          overSwing,
        };
        console.log('[SportsScene] PREVIEW drag →', {
          rawDx: dx.toFixed(1), rawDy: dy.toFixed(1),
          magnitude: magnitude.toFixed(1), dt: dt.toFixed(0),
          speedPxPerSec: speed.toFixed(0),
          overSwing,
          'input.direction.x': input.direction.x.toFixed(3),
          'input.direction.y': input.direction.y.toFixed(3),
          'input.power': input.power.toFixed(3),
        });
      }
    } else {
      // ── Experience / touch mode ─────────────────────────────────────────────
      // Require a meaningful swipe length to avoid accidental taps.
      if (magnitude < 20) return;

      const nx = dx / magnitude;
      const ny = dy / magnitude;
      const speed = magnitude / (dt / 1000);
      // 1000 px/s = power 1.0 ("full" swing). Power is not capped — swiping
      // faster produces power > 1.0, making the ball fly further; overshoot
      // the far boundary and it's out of bounds. Over-swing (≥2.5×) is a whiff.
      const power = Math.max(0.05, speed / 1000);
      // Over-swing: 7.0× the full-power speed (7000 px/s). Only a wild
      // uncontrolled flick causes a whiff — overshooting is already penalised
      // by the ball landing past the last ring.
      const overSwing = speed > 7000;
      input = {
        direction: {
          x: nx,
          // Same mapping as preview mode: natural upward swipe → optimal elevation,
          // straight-up swipe → 60° (safe), horizontal swipe → low shot.
          y: Math.max(0, Math.min(1, -ny * 0.5 + 0.25)),
        },
        power,
        overSwing,
      };
      console.log('[SportsScene] TOUCH swipe →', {
        rawDx: dx.toFixed(1), rawDy: dy.toFixed(1),
        magnitude: magnitude.toFixed(1), dt: dt.toFixed(0),
        speedPxPerSec: speed.toFixed(0),
        overSwing,
        'input.direction.x': input.direction.x.toFixed(3),
        'input.direction.y': input.direction.y.toFixed(3),
        'input.power': input.power.toFixed(3),
      });
    }

    this.listeningForShot = false;
    this.showSwipeHint.set(false);
    // OrbitControls state is owned entirely by the player (via lock icon).
    // We don't touch controls.enabled here — camera stays locked/unlocked
    // as the player left it. The next awaitNextShot() will re-arm immediately
    // if the camera is still locked.

    if (this.pendingShotResolve) {
      const resolve = this.pendingShotResolve;
      this.pendingShotResolve = null;
      resolve(input);
    }
  }

  // ─── Shot animation ────────────────────────────────────────────────────────────

  private animateShotArc(evaluation: ShotEvaluation): Promise<void> {
    return new Promise<void>((resolve) => {
      if (evaluation.isWhiff) {
        // Whiff: short wobble in place, no travel
        this.playWhiffAnimation().then(resolve);
        return;
      }

      const r = this.ballRadius();
      const params = this.physicsParams();
      if (!params) { resolve(); return; }

      const start = {
        x: this.ballPosition()[0],
        y: r + 0.05, // start slightly above ground — avoids cannon-es resting-contact zeroing vy
        z: this.ballPosition()[2],
      };

      // Rotate the evaluation's camera-local landing position into world space
      // using the camera direction captured at pointer-down.
      //
      // For GOAL sports (football, soccer, basketball, hockey):
      //   The ball must always visually reach the goal plane at currentAttemptDistance,
      //   regardless of power. Power only affects WHERE within the goal it lands (x, y).
      //   Using evaluation.finalPosition.z (power-derived) would make low-power shots
      //   stop far short of the goal — they'd look like ground rolls.
      //
      // For DISTANCE sports (golf, baseball):
      //   The landing distance IS the interesting result, so we use the physics distance.
      const isGoalSport = this.sportDef()?.scoringModel !== 'distance-bands';
      const localDist = isGoalSport
        ? this.currentAttemptDistance()  // always reach the goal plane
        : -evaluation.finalPosition.z;   // distance sports: use physics result

      const localLateral = evaluation.finalPosition.x; // positive = camera-right
      const fw = this.shotCameraForward;
      const rt = this.shotCameraRight;

      // For goal sports the ball arrives at the goal face height (y from evaluateShot).
      // For distance sports (golf, baseball) the ball lands on the ground (y = ballRadius).
      const endY = isGoalSport ? Math.max(r, evaluation.finalPosition.y) : r;

      const end = {
        x: start.x + fw.x * localDist + rt.x * localLateral,
        y: endY,
        z: start.z + fw.z * localDist + rt.z * localLateral,
      };

      if (isGoalSport) {
        // ── Goal sports: cannon-es physics synchronized with Three.js render loop
        //
        // Mirrors the freekick example's approach exactly:
        //   kickBall()  → ballBody.velocity.set(vx, vy, vz)
        //   animate()   → world.step(1/60) then ball.position.copy(ballBody.position)
        //
        // We compute the kick velocity using inverse kinematics (IK): given the
        // target endpoint (end) and desired arc height (arcHeightFactor × distance),
        // solve for the initial velocity that produces that parabola under gravity.
        // injectBeforeRender then steps the world every frame.
        const gravity = 4.9; // half-gravity: cinematic arc at mini-scale

        const world = new CANNON.World({ gravity: new CANNON.Vec3(0, -gravity, 0) });

        // No ground plane for goal sports: the ball arcs through the air toward the goal.
        // A ground body at y=0 causes resting-contact forces that zero out vy at step 0,
        // making the ball roll flat instead of arc. Distance sports use rAF, not cannon-es.

        // Ball body
        const ballBody = new CANNON.Body({
          mass: 0.45,
          shape: new CANNON.Sphere(r),
          linearDamping: 0.05,
          angularDamping: 0.1,
        });
        ballBody.position.set(start.x, start.y, start.z);

        // IK velocity: solve for initial velocity to hit 'end' with arc height = arcHeightFactor × dist
        //   peak height: h = vy₀² / (2g)  →  vy₀ = √(2gh)
        //   flight time: solve 0.5g·t² − vy₀·t + dy = 0  →  t = (vy₀ + √(vy₀²−2g·dy)) / g
        //   horizontal:  vx = dx/t,  vz = dz/t
        const dx = end.x - start.x;
        const dz = end.z - start.z;
        const dy = end.y - start.y;
        const hDist = Math.sqrt(dx * dx + dz * dz);
        const arcHeight = Math.max(hDist * params.arcHeightFactor, r * 4);
        const vy0 = Math.sqrt(2 * gravity * arcHeight);
        const disc = vy0 * vy0 - 2 * gravity * dy;
        const tFlight = disc >= 0 ? (vy0 + Math.sqrt(disc)) / gravity : (2 * vy0) / gravity;
        ballBody.velocity.set(
          tFlight > 0 ? dx / tFlight : 0,
          vy0,
          tFlight > 0 ? dz / tFlight : 0
        );
        world.addBody(ballBody);

        // ── DEBUG ──────────────────────────────────────────────────────────────
        console.log('[SHOT] Physics kick setup:', {
          start: { x: +start.x.toFixed(3), y: +start.y.toFixed(3), z: +start.z.toFixed(3) },
          end:   { x: +end.x.toFixed(3),   y: +end.y.toFixed(3),   z: +end.z.toFixed(3) },
          fw:    { x: +fw.x.toFixed(3), z: +fw.z.toFixed(3) },
          rt:    { x: +rt.x.toFixed(3), z: +rt.z.toFixed(3) },
          localDist:    +localDist.toFixed(3),
          localLateral: +localLateral.toFixed(3),
          evalFinalPos: {
            x: +evaluation.finalPosition.x.toFixed(3),
            y: +evaluation.finalPosition.y.toFixed(3),
            z: +evaluation.finalPosition.z.toFixed(3),
          },
          hDist:     +hDist.toFixed(3),
          arcHeight: +arcHeight.toFixed(3),
          vy0:       +vy0.toFixed(3),
          tFlight:   +tFlight.toFixed(3),
          kickVelocity: {
            vx: +(tFlight > 0 ? dx / tFlight : 0).toFixed(3),
            vy: +vy0.toFixed(3),
            vz: +(tFlight > 0 ? dz / tFlight : 0).toFixed(3),
          },
          scored: evaluation.scored,
          zone:   evaluation.zone,
        });

        // Hand off to injectBeforeRender for per-frame stepping + goal detection
        this.cannonWorld = world;
        this.cannonBallBody = ballBody;
        this.physicsGoalZ = end.z;
        this.physicsGoingNegZ = end.z < start.z;
        this.physicsEndPos = end;
        this.physicsOnDone = () => {
          this.animating = false;
          setTimeout(resolve, RESULT_DISPLAY_MS);
        };
        this.animating = true;
        this.physicsActive = true;
        return;
      }

      // ── Distance sports (golf-driving, baseball): rAF parabolic arc ──────────
      const arcPoints = this.physics.buildArcPoints(start, end, params, ARC_STEPS);
      this.animating = true;
      const total = arcPoints.length - 1;
      const startTime = performance.now();

      const tick = (now: number) => {
        const elapsed = now - startTime;
        const progress = Math.min(1, elapsed / ARC_DURATION_MS);
        const idx = Math.min(Math.floor(progress * total), total);
        this.ballPosition.set(arcPoints[idx]);
        if (progress < 1) {
          requestAnimationFrame(tick);
        } else {
          this.ballPosition.set(arcPoints[total]);
          this.animating = false;
          setTimeout(resolve, RESULT_DISPLAY_MS);
        }
      };
      requestAnimationFrame(tick);
    });
  }

  private playWhiffAnimation(): Promise<void> {
    return new Promise<void>((resolve) => {
      const r    = this.ballRadius();
      const base = this.ballPosition();
      // Keyframes: [x, y, z, time_ms] — slight wobble, ball barely moves.
      const frames: [number, number, number, number][] = [
        [base[0],        r,         base[2],        0   ],
        [base[0],        r * 1.4,   base[2] - 0.04, 80  ],
        [base[0] + 0.04, r * 0.6,   base[2] - 0.12, 160 ],
        [base[0] + 0.06, r * 0.15,  base[2] - 0.22, 260 ],
        [base[0] + 0.05, r,         base[2] - 0.32, 380 ],
        [base[0] + 0.03, r,         base[2] - 0.40, 520 ],
        [base[0],        r,         base[2] - 0.44, 680 ],
      ];
      const totalDuration = 680;
      const startTime = performance.now();

      const tick = (now: number) => {
        const elapsed = now - startTime;
        const progress = Math.min(1, elapsed / totalDuration);
        // Find the correct keyframe pair based on elapsed time
        let frame = frames[frames.length - 1];
        for (let i = frames.length - 1; i >= 0; i--) {
          if (frames[i][3] <= elapsed) { frame = frames[i]; break; }
        }
        this.ballPosition.set([frame[0], frame[1], frame[2]]);
        if (progress < 1) {
          requestAnimationFrame(tick);
        } else {
          setTimeout(resolve, RESULT_DISPLAY_MS);
        }
      };
      requestAnimationFrame(tick);
    });
  }

  // ─── Cleanup ──────────────────────────────────────────────────────────────────

  private removePointerListeners(): void {
    if (this.canvas) {
      this.canvas.removeEventListener('pointerdown', this.boundPointerDown);
      this.canvas.removeEventListener('pointerup', this.boundPointerUp);
      this.canvas = null;
    }
  }

  // Expose Math.PI for the template
  protected readonly Math = Math;
}
